import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import axios from 'axios';
import '../styles/BuyForm.css';

const BuyForm = () => {
  const [showPopup, setShowPopup] = useState(false);
  const location = useLocation();
  const { cartItems = [], isGuest = false, guestCartId = null } = location.state || {};
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    city: '',
    postalCode: '',
    address: '',
    phoneNumber: '',
    paymentMethod: '',
    guestEmail: ''
  });
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();
  const [deliveryPrice, setDeliveryPrice] = useState(0);
  const [deliveryLoading, setDeliveryLoading] = useState(false);
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

  // Helper function to construct proper image URL
  const getImageUrl = (imageUrl) => {
    if (!imageUrl) {
      return "/noImage.jpg"; // Fallback if no image URL is provided
    }
    // Check if the URL is already absolute
    if (imageUrl.startsWith('http')) {
      return imageUrl;
    }
    // Prefix with the server URL if the URL is relative
    return `${API_BASE_URL}${imageUrl}`;
  };

  const fetchDeliveryPrice = async () => {
    if (cartItems.length === 0) return;

    setDeliveryLoading(true);
    try {
      const totalPrice = cartItems.reduce((acc, item) => acc + item.retailPrice * item.quantity, 0);

      // Prepare items data for weight calculation
      const items = cartItems.map(item => ({
        productId: item.productId,
        productKey: item.productKey || null,
        productCode: item.productCode || null,
        quantity: item.quantity,
        price: item.price || item.retailPrice,
        retailPrice: item.retailPrice
      }));

      const response = await axios.post(`${API_BASE_URL}/orders/calculate-delivery`, {
        items,
        totalPrice,
        paymentMethod: formData.paymentMethod // Add payment method to request
      }, {
        withCredentials: true
      });

      setDeliveryPrice(response.data.deliveryCost || 0);
    } catch (error) {
      console.error('Error calculating delivery:', error);
      setDeliveryPrice(0); // Set to 0 on error for store pickup
    } finally {
      setDeliveryLoading(false);
    }
  };

useEffect(() => {
  fetchDeliveryPrice();
}, [cartItems, formData.paymentMethod]); // Add payment method as dependency// Recalculate whenever cart items change

  useEffect(() => {
    const fetchUserData = async () => {
      // For guests, skip authentication check
      if (isGuest) {
        setLoading(false);
        return;
      }

      try {
        // Make request with cookies (withCredentials: true)
        const response = await axios.get(`${API_BASE_URL}/auth/user`, {
          withCredentials: true, // This sends cookies automatically
        });

        setUser(response.data);
        setFormData(prev => ({
          ...prev,
          firstName: response.data.firstName || '',
          lastName: response.data.lastName || '',
          city: response.data.address?.city || '',
          postalCode: response.data.address?.postalCode || '',
          address: response.data.address?.streetAddress || '',
          phoneNumber: response.data.phoneNumber || '',
        }));
      } catch (error) {
        console.error('Error fetching user data:', error);
        // Only redirect to login if we get 401 (unauthorized)
        if (error.response?.status === 401) {
          navigate('/login');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchUserData();
  }, [navigate, isGuest, API_BASE_URL]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (cartItems.length === 0) {
      alert('Your cart is empty!');
      return;
    }

      if (!formData.paymentMethod) {
        alert('Molimo izaberite metodu plaćanja');
        return;
      }

    if (isGuest) {
      if (!formData.guestEmail || !/^\S+@\S+\.\S+$/.test(formData.guestEmail)) {
        alert('Please enter a valid email address');
        return;
      }
      if (!guestCartId) {
        alert('Missing cart session. Please add items to cart again.');
        return;
      }
    }

    setSubmitting(true);

    try {
      const orderData = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        city: formData.city,
        postalCode: formData.postalCode,
        address: formData.address,
        phoneNumber: formData.phoneNumber,
        paymentMethod: formData.paymentMethod,
        items: cartItems.map(item => ({
          productId: item.productId,
          productKey: item.productKey || null,
          productCode: item.productCode || null,
          productName: item.name,
          imageUrl: item.imageUrl || (item.images && item.images[0]?.url) ||
                   'https://localhost:3000/default-product-image.jpg',
          quantity: item.quantity,
          price: item.price || item.retailPrice,
          retailPrice: item.retailPrice
        })),
        totalPrice: cartItems.reduce((acc, item) => acc + item.retailPrice * item.quantity, 0) + deliveryPrice,
        ...(isGuest && {
          guestEmail: formData.guestEmail,
          guestFirstName: formData.firstName,
          guestLastName: formData.lastName,
          guestCartId: guestCartId
        })
      };

      // 1. Place order first
      await axios.post(
        `${API_BASE_URL}/orders`,
        orderData,
        {
          withCredentials: true, // Use cookies for authentication
          ...(!isGuest && { withCredentials: true }) // Ensure cookies are sent for logged-in users
        }
      );

      // 2. Clear cart only after successful order
      try {
        const clearUrl = isGuest
          ? `${API_BASE_URL}/clear?guestCartId=${guestCartId}`
          : `${API_BASE_URL}/clear?userId=${user?.id}`;

        await axios.delete(
          clearUrl,
          {
            withCredentials: true, // Use cookies for authentication
          }
        );
      } catch (clearError) {
        console.error('Cart clearance failed:', clearError);
      }

      // 3. Show popup without automatic redirect
      setShowPopup(true);

    } catch (error) {
      console.error('Full error details:', error);
      let errorMessage = 'Order placement failed. Please try again.';

      if (error.response) {
        errorMessage = error.response.data.message ||
                       error.response.data ||
                       `Server error: ${error.response.status}`;
      }

      alert(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading && !isGuest) {
    return (
      <div className="buy-form-container">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Loading user data...</p>
        </div>
      </div>
    );
  }

  if (cartItems.length === 0) {
    return (
      <div className="buy-form-container">
        <h2>Checkout</h2>
        <div className="empty-cart-message">
          <p>Vaša korpa je prazna!</p>
          <button onClick={() => navigate('/')}>Nastavite kupovinu</button>
        </div>
      </div>
    );
  }

  // Calculate subtotal (without delivery)
  const subtotal = cartItems.reduce((acc, item) => acc + item.retailPrice * item.quantity, 0);
  // Calculate total (with delivery)
  const total = subtotal + deliveryPrice;

  return (
    <div className="buy-form-container">
      <h2>Checkout</h2>
      <form onSubmit={handleSubmit}>
        {isGuest && (
          <div className="form-group">
            <label>Email (za potvrdu porudžbine)</label>
            <input
              type="email"
              name="guestEmail"
              value={formData.guestEmail}
              onChange={handleChange}
              required
              disabled={submitting}
            />
          </div>
        )}

        <div className="form-row">
          <div className="form-column">
            <div className="form-group">
              <label>Ime</label>
              <input
                type="text"
                name="firstName"
                value={formData.firstName}
                onChange={handleChange}
                required
                disabled={submitting}
              />
            </div>
          </div>
          <div className="form-column">
            <div className="form-group">
              <label>Prezime</label>
              <input
                type="text"
                name="lastName"
                value={formData.lastName}
                onChange={handleChange}
                required
                disabled={submitting}
              />
            </div>
          </div>
        </div>

        <div className="form-row">
          <div className="form-column">
            <div className="form-group">
              <label>Grad</label>
              <input
                type="text"
                name="city"
                value={formData.city}
                onChange={handleChange}
                required
                disabled={submitting}
              />
            </div>
          </div>
          <div className="form-column">
            <div className="form-group">
              <label>Poštanski broj</label>
              <input
                type="text"
                name="postalCode"
                value={formData.postalCode}
                onChange={handleChange}
                required
                disabled={submitting}
              />
            </div>
          </div>
        </div>

        <div className="form-group">
          <label>Adresa</label>
          <input
            type="text"
            name="address"
            value={formData.address}
            onChange={handleChange}
            required
            disabled={submitting}
          />
        </div>

        <div className="form-row">
          <div className="form-column">
            <div className="form-group">
              <label>Broj telefona</label>
              <input
                type="text"
                name="phoneNumber"
                value={formData.phoneNumber}
                onChange={handleChange}
                required
                disabled={submitting}
              />
            </div>
          </div>
          <div className="form-column">
             <div className="form-group payment-method-group">
               <label>Metoda plaćanja</label>
               <select
                 name="paymentMethod"
                 value={formData.paymentMethod}
                 onChange={handleChange}
                 required
                 disabled={submitting}
               >
                 <option value="">Izaberite metodu plaćanja</option>
                 <option value="cashondelivery">Plaćanje pouzećem</option>
                 <option value="storepickup">Preuzimanje u radnji</option>
               </select>
             </div>
           </div>
        </div>

        <div className="cart-summary">
          <h3>Podaci o kupovini</h3>
          <div className="cart-items">
            {cartItems.map((item, index) => {
              // Extract image URL using the same approach as in CartDropdown
              const imageUrl = item.imageUrl || item.images?.[0]?.imageUrl;

              return (
                <div key={index} className="cart-item">
                  <div className="item-image">
                    <img
                      src={getImageUrl(imageUrl)}
                      alt={item.name || "Product"}
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = "/noImage.jpg"; // Fallback image
                      }}
                    />
                  </div>
                  <div className="item-details">
                    <Link to={`/home/product/${item.productId}`} className="product-name" title={item.name}>
                      {item.name}
                    </Link>
                    <div className="item-info">
                      <span className="item-quantity">Količina: {item.quantity}</span>
                      <span className="item-price">Cena: {item.retailPrice.toFixed(2)} RSD</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="price-summary">
            <div className="price-row">
              <span>Ukupno proizvodi:</span>
              <span>{subtotal.toFixed(2)} RSD</span>
            </div>
            <div className="price-row">
              <span>Cena dostave:</span>
              <span>
                {deliveryLoading ? (
                  <span className="loading-text">Računa se...</span>
                ) : (
                  `${deliveryPrice.toFixed(2)} RSD`
                )}
              </span>
            </div>
            <div className="price-row total-price">
              <strong>Ukupno za plaćanje: </strong>
              <strong>{total.toFixed(2)} RSD</strong>
            </div>
          </div>
        </div>

        <button
          type="submit"
          className="order-button"
          disabled={submitting || deliveryLoading}
        >
          {submitting ? 'Obrađuje se...' : 'Poruči'}
        </button>
      </form>

      {showPopup && (
        <>
          <div className="popup-overlay"></div>
          <div className="popup">
            <button
              className="popup-close-button"
              onClick={() => {
                setShowPopup(false);
                navigate('/home');
              }}
            >
              &times;
            </button>
            <div className="popup-content">
              Narudžbina uspešno primljena. Očekujte potvrdu na vašem mejlu.
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default BuyForm;